/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-09-17 15:29:45
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import './index.scss'
import { Button } from 'antd'
import { fetchRecommendSearchKeywords } from '../../../actions/photo'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import TagsInputDropdown from './TagsInputDropdown'
import sensors from '../../../utils/sensors'
import { useSearchHistory } from '../../../app/hooks/useSearchKeywordsHistory'
import TagsInput from './TagsInput'

const ACCEPTANCE_SEARCHED_KEY = 'acceptanceSearched'
const CONSTRUCTION_INDUSTRY_CODE = ['020010', '020020', '020030']

interface KeywordsProps {
  value: string[]
  onChange: (value: string[]) => void
  onSensorOfClickEvent: (clickItem: string, restProperties: any) => void
  onSubmit: () => void
  onInputFocusChange?: (isFocused: boolean) => void // 新增：输入框聚焦状态变化回调
}

const Keywords: React.FC<KeywordsProps> = ({
  value = [],
  onChange = () => {},
  onSensorOfClickEvent = () => {},
  onSubmit = () => {},
  onInputFocusChange = () => {} // 新增：输入框聚焦状态变化回调
}) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const industryCode = useAppSelector((state) =>
    state.group.industryCode
  )
  const [recommendKeywords, setRecommendKeywords] = useState([])
  const [inputValue, setInputValue] = useState('')
  const { history: searchHistory = [] } = useSearchHistory()

  const handleFetchRecommendKeywords = useCallback(() => {
    dispatch(
      fetchRecommendSearchKeywords({
        groupID
      })
    ).then((data: any) => {
      if (data.status === 0) {
        //3.0.45 如果是建筑3行业团队，且搜索推荐中没有验收，插入验收。搜索验收后，不在插入。
        const searchRecommend = data.searchRecommend.slice(0, 3)
        const acceptanceSearched = localStorage.getItem(ACCEPTANCE_SEARCHED_KEY)
        if (
          CONSTRUCTION_INDUSTRY_CODE.indexOf(industryCode || '') > -1 && //建筑三类的团队用户
          searchRecommend.length > 0 && //有其他外置的猜你想搜关键词
          !acceptanceSearched //未搜索过 验收
        ) {
          if (searchRecommend.indexOf('验收') === -1) {
            searchRecommend.splice(0, 0, '验收')
          }
        }
        setRecommendKeywords(searchRecommend.slice(0, 3))
        sensors.track('web_workgroup_picspage_click', {
          clickItem: 'enterPage',
          historyRecordNum: searchHistory.length,
          recommendNum: searchRecommend.slice(0, 3).length,
          track_sign: 'aworkgroup.b4343.action.web_workgroup_picspage_click',
          requirement_id: 108
        })
      }
    })
  }, [groupID, industryCode, searchHistory, dispatch])

  const handleUseRecommendKeywords = (keyword: string) => {
    // 如果已经存在，不再添加
    if (value.indexOf(keyword) > -1) {
      return
    }
    // 小于3个，追加
    if (value.length < 3) {
      onValueChange([...value, keyword])
    } else {
      // 3个，替换最后一个
      onValueChange([...value.slice(0, -1), keyword])
    }
  }

  const recommendKeywordsWithHistory = useMemo(() => {
    if (searchHistory?.length === 0) {
      return recommendKeywords.map((item) => ({
        word: item,
        type: 'recommend'
      }))
    }
    const historyWords = searchHistory.map((item) => ({
      word: item.word,
      type: 'history'
    }))
    const recommendWords = recommendKeywords
      .filter((item) => !searchHistory.includes(item)) //去除与历史记录重复的
      .map((item) => ({
        word: item,
        type: 'recommend'
      }))
    return [...historyWords, ...recommendWords]
  }, [searchHistory, recommendKeywords])

  const onValueChange = (keywords: string[]) => {
    setInputValue('')
    onChange(keywords)
  }

  useEffect(() => {
    handleFetchRecommendKeywords()
  }, [])

  return (
    <div className="keywords-wrapper">
      <p className="label">筛选内容</p>
      <TagsInput
        tags={value}
        onChange={onValueChange}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSensorOfClickEvent={onSensorOfClickEvent}
        onFocusChange={onInputFocusChange} // 新增：传递聚焦状态变化回调
      />
      <TagsInputDropdown
        tags={value}
        input={inputValue}
        onSubmitTag={onValueChange}
        onSensorOfClickEvent={onSensorOfClickEvent}
        onSubmit={onSubmit}
      />
      {recommendKeywordsWithHistory.length > 0 && (
        <div className="quickpicker-wrapper">
          猜你想搜{' '}
          <div>
            {recommendKeywordsWithHistory.map((item, index) => {
              return (
                <Button
                  type="link"
                  size="small"
                  title={item.word}
                  className="recommend-keywords"
                  onClick={() => {
                    handleUseRecommendKeywords(item.word)
                    onSensorOfClickEvent('recommendSearch', {
                      clickSearchContent: item,
                      bool1: item.type === 'history'
                    })
                  }}
                  key={index}
                >
                  {item.word}
                </Button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default Keywords
